import Swiper from "swiper";
import {
  A11y,
  FreeMode,
  Keyboard,
  Mousewheel,
  Scrollbar,
} from "swiper/modules";
import { initializeOnApproach } from "../../../resources/js/_initialize-on-approach";

document.addEventListener("DOMContentLoaded", function () {
  const carousels = document.querySelectorAll(".card-carousel");

  carousels.forEach((carousel) => {
    initializeOnApproach(carousel, () => {
      initializeCardCarouselPatternReveal(carousel);
      initializeCardCarousel(carousel);
    });
  });
});

document.addEventListener("blockEditorInit", function (event) {
  const block = event.detail.block;

  if (!block) {
    return;
  }

  if (block.matches && block.matches(".card-carousel")) {
    initializeCardCarouselPatternReveal(block);
    initializeCardCarousel(block);
  }

  block.querySelectorAll(".card-carousel").forEach((carousel) => {
    initializeCardCarouselPatternReveal(carousel);
    initializeCardCarousel(carousel);
  });
});

function initializeCardCarouselPatternReveal(carouselElement) {
  const patternElement = carouselElement.querySelector(
    ".section-pattern__image",
  );

  if (
    !patternElement ||
    carouselElement.dataset.cardCarouselPatternInitialized === "true"
  ) {
    return;
  }

  carouselElement.dataset.cardCarouselPatternInitialized = "true";

  if (!("IntersectionObserver" in window)) {
    carouselElement.classList.add("card-carousel--pattern-visible");
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        carouselElement.classList.add("card-carousel--pattern-visible");
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.25,
      rootMargin: "0px 0px -10% 0px",
    },
  );

  observer.observe(carouselElement);
}

function initializeCardCarousel(carouselElement) {
  const railElement = carouselElement.querySelector(".card-carousel__rail");
  const swiperContainer = carouselElement.querySelector(
    ".card-carousel__swiper",
  );
  const progressTrackElement = carouselElement.querySelector(
    ".card-carousel__scrollbar",
  );

  if (
    !swiperContainer ||
    swiperContainer.dataset.cardCarouselInitialized === "true"
  ) {
    return;
  }

  const slides = swiperContainer.querySelectorAll(".swiper-slide");

  if (slides.length < 2) {
    carouselElement.classList.add("card-carousel--locked");
    return;
  }

  const updateRailWidth = () => {
    if (!railElement) {
      return;
    }

    const railBounds = railElement.getBoundingClientRect();
    // Use clientWidth (excludes the vertical scrollbar) so the full-bleed rail
    // ends exactly at the visible edge — otherwise the last card is clipped.
    const viewportWidth =
      document.documentElement.clientWidth || window.innerWidth;
    const availableWidth = Math.max(0, viewportWidth - railBounds.left);

    railElement.style.setProperty(
      "--card-carousel-visible-width",
      `${availableWidth}px`,
    );

    // The rail bleeds full-width to the viewport edge, but the scrollbar should
    // stop at the page content edge. Measure how far the rail overshoots the
    // container's inner right edge and expose it for the scrollbar margin.
    const container = carouselElement.querySelector(".container-wide");

    if (container) {
      const containerStyles = window.getComputedStyle(container);
      const paddingRight = parseFloat(containerStyles.paddingRight) || 0;
      const contentRight =
        container.getBoundingClientRect().right - paddingRight;
      const bleedRight = Math.max(0, viewportWidth - contentRight);

      railElement.style.setProperty(
        "--card-carousel-rail-bleed",
        `${bleedRight}px`,
      );
    }
  };

  updateRailWidth();
  swiperContainer.dataset.cardCarouselInitialized = "true";

  const syncLockState = (swiperInstance) => {
    carouselElement.classList.toggle(
      "card-carousel--locked",
      swiperInstance.isLocked,
    );
  };

  const swiper = new Swiper(swiperContainer, {
    modules: [A11y, FreeMode, Keyboard, Mousewheel, Scrollbar],
    slidesPerView: 4.1,
    spaceBetween: 24,
    slidesOffsetAfter: 96,
    speed: 700,
    freeMode: {
      enabled: true,
      momentumRatio: 0.35,
    },
    watchOverflow: true,
    grabCursor: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    mousewheel: {
      enabled: true,
      forceToAxis: true,
      releaseOnEdges: true,
    },
    scrollbar: progressTrackElement
      ? {
          el: progressTrackElement,
          dragClass: "card-carousel__scrollbar-thumb",
          draggable: true,
          hide: false,
          snapOnRelease: false,
        }
      : false,
    a11y: {
      enabled: true,
      prevSlideMessage: "Previous cards",
      nextSlideMessage: "Next cards",
    },
    breakpoints: {
      0: {
        slidesPerView: 1.2,
        spaceBetween: 16,
        slidesOffsetAfter: 48,
      },
      520: {
        slidesPerView: 1.2,
        spaceBetween: 20,
        slidesOffsetAfter: 64,
      },
      1150: {
        slidesPerView: 2.2,
        spaceBetween: 24,
        slidesOffsetAfter: 96,
      },
      1250: {
        slidesPerView: 3.1,
        spaceBetween: 24,
        slidesOffsetAfter: 96,
      },
      1450: {
        slidesPerView: 4.1,
        spaceBetween: 24,
        slidesOffsetAfter: 96,
      },
    },
    on: {
      init() {
        syncLockState(this);
      },
      resize() {
        syncLockState(this);
      },
      lock() {
        syncLockState(this);
      },
      unlock() {
        syncLockState(this);
      },
    },
  });

  requestAnimationFrame(() => {
    updateRailWidth();
    swiper.update();
    syncLockState(swiper);
  });

  window.addEventListener("resize", () => {
    updateRailWidth();
    swiper.update();
    syncLockState(swiper);
  });
}
